import * as React from 'react';
import {
  Target,
  Mic2,
  Video,
  Sparkles,
  MessageSquare,
  Loader2,
  RefreshCw,
  Copy,
  Check,
  Upload,
  PenLine,
  Send,
  Image as ImageIcon,
  Scissors,
  Newspaper,
  Wand2,
  Download,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import HotspotsPage from '@/pages/hotspots';
import {
  getViralTopics,
  submitIpMaterial,
  getIpMaterials,
  generateTranscript,
  startWechatArticle,
  requestInterview,
  rewriteContent,
  getInterviewOutline,
  generatePoster,
  getPosters,
  getPosterTemplates,
  uploadImage,
} from '@/api/client';

type ViralTopic = {
  title: string;
  angle: string;
  recommendedFormat: string;
  status: string;
  totalScore: number;
  recommendationReason: string;
  sources: Array<{ type: string; path: string }>;
  scoreBreakdown: Record<string, number>;
};

type IpMaterial = {
  id: number;
  type: 'audio' | 'text';
  content: string;
  file_name: string;
  note: string;
  status: string;
  created_at: string;
};

const DURATIONS = ['1min', '3min', '5min', '8min', '12min'];

export default function ContentPage() {
  const [activeTab, setActiveTab] = React.useState<
    'viral' | 'ip' | 'workbench' | 'rewrite' | 'interview' | 'poster'
  >('viral');

  // 爆款选题
  const [viralTopics, setViralTopics] = React.useState<ViralTopic[]>([]);
  const [viralSummary, setViralSummary] = React.useState<{
    libraryCount: number;
    ipMaterialCount: number;
    leadCount: number;
    hotspotCount: number;
  } | null>(null);
  const [viralLoading, setViralLoading] = React.useState(true);

  // IP 语料
  const [ipText, setIpText] = React.useState('');
  const [ipNote, setIpNote] = React.useState('');
  const [ipAudioName, setIpAudioName] = React.useState('');
  const [ipMaterials, setIpMaterials] = React.useState<IpMaterial[]>([]);
  const [ipSubmitting, setIpSubmitting] = React.useState(false);
  const [ipStatus, setIpStatus] = React.useState('');

  // 短视频工作台
  const [duration, setDuration] = React.useState('3min');
  const [transcriptResult, setTranscriptResult] = React.useState('');
  const [transcriptStatus, setTranscriptStatus] = React.useState('');
  const [transcriptBusy, setTranscriptBusy] = React.useState(false);

  // 改写
  const [rewriteText, setRewriteText] = React.useState('');
  const [rewriteStyle, setRewriteStyle] = React.useState('casual');
  const [rewriteResult, setRewriteResult] = React.useState('');
  const [rewriting, setRewriting] = React.useState(false);
  const [rewriteCopied, setRewriteCopied] = React.useState(false);

  // 采访提纲 / 米粒采访
  const [topic, setTopic] = React.useState('');
  const [interviewMode, setInterviewMode] = React.useState<'一般采访' | '深度采访'>('一般采访');
  const [outline, setOutline] = React.useState('');
  const [outlineLoading, setOutlineLoading] = React.useState(false);
  const [interviewBusy, setInterviewBusy] = React.useState(false);
  const [interviewStatus, setInterviewStatus] = React.useState('');

  // 海报生成
  const [posterText, setPosterText] = React.useState('');
  const [posterSubtitle, setPosterSubtitle] = React.useState('');
  const [posterSize, setPosterSize] = React.useState('3:4');
  const [posterTemplateId, setPosterTemplateId] = React.useState('minimal');
  const [posterTemplates, setPosterTemplates] = React.useState<Array<{id: string; name: string; description: string; category: string; thumbnail: string; sizes: string[]; default_size: string; image_elements?: Array<{id: string; field: string; label: string; optional: boolean}>}>>([]);
  const [posterScheme, setPosterScheme] = React.useState<'template' | 'aigc'>('template');
  const [posterGenerating, setPosterGenerating] = React.useState(false);
  const [posterImageUrl, setPosterImageUrl] = React.useState('');
  const [posterError, setPosterError] = React.useState('');
  const [posterHistory, setPosterHistory] = React.useState<any[]>([]);
  const [posterImages, setPosterImages] = React.useState<Record<string, string>>({});
  const [uploadingField, setUploadingField] = React.useState('');

  // ─── 爆款选题 ────────────────────────────────────────────────

  const loadViralTopics = React.useCallback(() => {
    setViralLoading(true);
    getViralTopics(10)
      .then((data) => {
        setViralTopics(data.items || []);
        setViralSummary(data.sourceSummary || null);
      })
      .catch(() => {})
      .finally(() => setViralLoading(false));
  }, []);

  // ─── IP 语料 ────────────────────────────────────────────────

  const loadIpMaterials = React.useCallback(() => {
    getIpMaterials()
      .then((data) => setIpMaterials(data.items || []))
      .catch(() => {});
  }, []);

  React.useEffect(() => {
    loadViralTopics();
    loadIpMaterials();
  }, [loadViralTopics, loadIpMaterials]);

  const handleSubmitIpText = React.useCallback(async () => {
    if (!ipText.trim()) return;
    setIpSubmitting(true);
    setIpStatus('');
    try {
      const data = await submitIpMaterial({
        type: 'text',
        payload: { text: ipText.trim(), note: ipNote.trim() },
      });
      setIpStatus(data.status || '已提交');
      setIpText('');
      setIpNote('');
      loadIpMaterials();
      loadViralTopics();
    } catch (err) {
      setIpStatus(err instanceof Error ? err.message : '提交失败');
    } finally {
      setIpSubmitting(false);
    }
  }, [ipText, ipNote, loadIpMaterials, loadViralTopics]);

  const handleSubmitIpAudio = React.useCallback(async () => {
    if (!ipAudioName) return;
    setIpSubmitting(true);
    setIpStatus('');
    try {
      const data = await submitIpMaterial({
        type: 'audio',
        payload: { fileName: ipAudioName, note: ipNote.trim() },
      });
      setIpStatus(data.status || '已提交');
      setIpAudioName('');
      setIpNote('');
      loadIpMaterials();
      loadViralTopics();
    } catch (err) {
      setIpStatus(err instanceof Error ? err.message : '提交失败');
    } finally {
      setIpSubmitting(false);
    }
  }, [ipAudioName, ipNote, loadIpMaterials, loadViralTopics]);

  // ─── 短视频工作台 ────────────────────────────────────────────

  const handleGenerateTranscript = React.useCallback(
    async (topicText = '短视频逐字稿') => {
      setTranscriptBusy(true);
      setTranscriptStatus('');
      try {
        const data = await generateTranscript(duration, topicText);
        setTranscriptResult(data.draft || '');
        setTranscriptStatus(`${topicText}：${data.status || '逐字稿任务已创建'}`);
        setActiveTab('workbench');
      } catch (err) {
        setTranscriptStatus(err instanceof Error ? err.message : '生成失败');
      } finally {
        setTranscriptBusy(false);
      }
    },
    [duration]
  );

  const handleWechatArticle = React.useCallback(async () => {
    setTranscriptBusy(true);
    setTranscriptStatus('');
    try {
      const data = await startWechatArticle();
      setTranscriptStatus(data.status || '公众号生成任务已启动');
    } catch (err) {
      setTranscriptStatus(err instanceof Error ? err.message : '启动失败');
    } finally {
      setTranscriptBusy(false);
    }
  }, []);

  // ─── 改写 ────────────────────────────────────────────────────

  const handleRewrite = React.useCallback(async () => {
    if (!rewriteText.trim()) return;
    setRewriting(true);
    setRewriteResult('');
    try {
      const data = await rewriteContent(rewriteText, rewriteStyle);
      setRewriteResult(data.result || '改写完成');
    } catch (err) {
      setRewriteResult(err instanceof Error ? err.message : '改写失败');
    } finally {
      setRewriting(false);
    }
  }, [rewriteText, rewriteStyle]);

  // ─── 采访提纲 / 米粒采访 ────────────────────────────────────

  const handleOutline = React.useCallback(async () => {
    if (!topic.trim()) return;
    setOutlineLoading(true);
    setOutline('');
    setInterviewStatus('');
    try {
      const data = await getInterviewOutline(topic.trim(), interviewMode);
      const questions: string[] = data.questions || [];
      setOutline(questions.map((q, i) => `${i + 1}. ${q}`).join('\n'));
    } catch (err) {
      setOutline(err instanceof Error ? err.message : '生成失败');
    } finally {
      setOutlineLoading(false);
    }
  }, [topic, interviewMode]);

  const handleStartInterview = React.useCallback(async () => {
    const questions = (outline || '')
      .split('\n')
      .map((line) => line.replace(/^\d+[.、]\s*/, '').trim())
      .filter(Boolean);
    if (!questions.length) {
      setInterviewStatus('请先生成采访提纲');
      return;
    }
    setInterviewBusy(true);
    setInterviewStatus('');
    try {
      const data = await requestInterview({ topic: topic.trim() || '米粒采访', mode: interviewMode, questions });
      setInterviewStatus(`${data.status || '采访任务已创建'}${data.interviewKey ? `（${data.interviewKey}）` : ''}`);
    } catch (err) {
      setInterviewStatus(err instanceof Error ? err.message : '创建失败');
    } finally {
      setInterviewBusy(false);
    }
  }, [outline, topic, interviewMode]);

  const startInterviewFromTopic = React.useCallback((viralTopic: string) => {
    setTopic(viralTopic);
    setOutline('');
    setInterviewStatus('');
    setActiveTab('interview');
  }, []);

  // ─── 海报生成 ────────────────────────────────────────────────

  const loadPosters = React.useCallback(() => {
    getPosters(20)
      .then((data) => setPosterHistory(data.items || []))
      .catch(() => {});
    getPosterTemplates()
      .then((data) => {
        setPosterTemplates(data.items || []);
        if (data.items?.length && !data.items.find((t) => t.id === posterTemplateId)) {
          setPosterTemplateId(data.items[0].id);
        }
      })
      .catch(() => {});
  }, []);

  React.useEffect(() => {
    loadPosters();
  }, [loadPosters]);

  const handleGeneratePoster = React.useCallback(async () => {
    if (!posterText.trim()) return;
    setPosterGenerating(true);
    setPosterError('');
    setPosterImageUrl('');
    try {
      const data = await generatePoster({
        text: posterText.trim(),
        subtitle: posterSubtitle.trim(),
        size: posterSize,
        template_id: posterTemplateId,
        scheme: posterScheme,
        image_urls: posterImages,
      });
      setPosterImageUrl(data.image_url);
      loadPosters();
    } catch (err) {
      setPosterError(err instanceof Error ? err.message : '生成失败');
    } finally {
      setPosterGenerating(false);
    }
  }, [posterText, posterSubtitle, posterSize, posterTemplateId, posterScheme, loadPosters]);

  // ─── 复制 ────────────────────────────────────────────────────

  const handleCopy = React.useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setRewriteCopied(true);
      setTimeout(() => setRewriteCopied(false), 2000);
    } catch {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setRewriteCopied(true);
      setTimeout(() => setRewriteCopied(false), 2000);
    }
  }, []);

  const tabs = [
    { key: 'viral' as const, label: '爆款选题', icon: Target },
    { key: 'ip' as const, label: 'IP语料', icon: Mic2 },
    { key: 'workbench' as const, label: '短视频工作台', icon: Video },
    { key: 'rewrite' as const, label: '内容改写', icon: Sparkles },
    { key: 'interview' as const, label: '采访提纲', icon: MessageSquare },
    { key: 'poster' as const, label: '海报生成', icon: ImageIcon },
  ];

  return (
    <div className="grid gap-6 xl:grid-cols-3">
      {/* 左栏 2/3：内容工作台原有内容 */}
      <div className="min-w-0 space-y-6 xl:col-span-2">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">内容中心</h1>
            <p className="mt-1 text-sm text-muted-foreground">IP语料沉淀、爆款选题、逐字稿、封面剪辑、热点追踪、AI 改写和采访提纲生成</p>
          </div>
        </div>

      {/* Tab 导航 */}
      <div className="mb-6 flex flex-wrap gap-1 rounded-lg border bg-muted/30 p-1">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>


      {/* ─── 爆款选题 ─── */}
      {activeTab === 'viral' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>爆款选题</CardTitle>
                <CardDescription>
                  {viralSummary
                    ? `已读取内容库 ${viralSummary.libraryCount} 条、IP语料 ${viralSummary.ipMaterialCount} 条、线索 ${viralSummary.leadCount} 条、外部热点 ${viralSummary.hotspotCount} 条`
                    : '基于内容库、IP语料、线索和外部热点的六维评分推荐'}
                </CardDescription>
              </div>
              <Button variant="ghost" size="icon" onClick={loadViralTopics}>
                <RefreshCw className={`h-4 w-4 ${viralLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {viralLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-20 rounded" />)}
              </div>
            ) : viralTopics.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <Target className="mb-2 h-6 w-6" />
                <p className="text-sm">暂无可推荐选题</p>
                <p className="mt-1 text-xs">先到「IP语料」或内容库添加内容，系统会自动计算推荐</p>
              </div>
            ) : (
              <div className="divide-y">
                {viralTopics.map((topicItem, index) => (
                  <div key={`${topicItem.title}-${index}`} className="flex items-start gap-3 py-3">
                    <span className="flex h-10 w-10 flex-shrink-0 flex-col items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <span className="text-[9px] font-bold opacity-70">{String(index + 1).padStart(2, '0')}</span>
                      <span className="text-sm font-extrabold leading-none">{topicItem.totalScore}</span>
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium line-clamp-2">{topicItem.title}</div>
                      <div className="mt-1 text-xs text-muted-foreground">{topicItem.recommendationReason}</div>
                      {topicItem.angle && (
                        <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Badge variant="secondary" className="text-[10px]">{topicItem.recommendedFormat || '待定'}</Badge>
                          <span className="line-clamp-1">{topicItem.angle}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-shrink-0 flex-col gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => startInterviewFromTopic(topicItem.title)}
                      >
                        <MessageSquare className="h-3.5 w-3.5" />
                        米粒采访
                      </Button>
                      <Button
                        size="sm"
                        className="h-7 text-xs"
                        disabled={transcriptBusy}
                        onClick={() => handleGenerateTranscript(topicItem.title)}
                      >
                        <Send className="h-3.5 w-3.5" />
                        生成逐字稿
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ─── IP 语料 ─── */}
      {activeTab === 'ip' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>IP 语料输入</CardTitle>
              <CardDescription>把老板的表达、经历、判断和案例沉淀为短视频原料</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-lg border p-4">
                  <div className="mb-3 flex items-center gap-2">
                    <Upload className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">上传录音文件</span>
                  </div>
                  <Input
                    type="file"
                    accept="audio/*"
                    className="cursor-pointer"
                    onChange={(e) => setIpAudioName(e.target.files?.[0]?.name || '')}
                  />
                  <Input
                    className="mt-2"
                    placeholder="补充说明：这段录音是什么场景、适合转成什么内容"
                    value={ipNote}
                    onChange={(e) => setIpNote(e.target.value)}
                  />
                  <Button
                    className="mt-3 w-full"
                    size="sm"
                    disabled={ipSubmitting || !ipAudioName}
                    onClick={handleSubmitIpAudio}
                  >
                    {ipSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    提交录音语料
                  </Button>
                </div>
                <div className="rounded-lg border p-4">
                  <div className="mb-3 flex items-center gap-2">
                    <PenLine className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">文字输入</span>
                  </div>
                  <textarea
                    className="min-h-[96px] w-full rounded-md border bg-background p-3 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    placeholder="输入观点、案例、经历、直播复盘或短视频口播素材"
                    value={ipText}
                    onChange={(e) => setIpText(e.target.value)}
                  />
                  <Input
                    className="mt-2"
                    placeholder="补充说明（可选）"
                    value={ipNote}
                    onChange={(e) => setIpNote(e.target.value)}
                  />
                  <Button
                    className="mt-3 w-full"
                    size="sm"
                    disabled={ipSubmitting || !ipText.trim()}
                    onClick={handleSubmitIpText}
                  >
                    {ipSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />}
                    提交文字语料
                  </Button>
                </div>
              </div>
              {ipStatus && <p className="text-sm text-muted-foreground">{ipStatus}</p>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>已沉淀语料</CardTitle>
                  <CardDescription>共 {ipMaterials.length} 条，已进入综合语料池参与爆款选题评分</CardDescription>
                </div>
                <Button variant="ghost" size="icon" onClick={loadIpMaterials}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {ipMaterials.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                  <Mic2 className="mb-2 h-6 w-6" />
                  <p className="text-sm">还没有语料，从上方输入第一条</p>
                </div>
              ) : (
                <div className="divide-y">
                  {ipMaterials.map((m) => (
                    <div key={m.id} className="flex items-start gap-3 py-3">
                      <Badge variant={m.type === 'audio' ? 'secondary' : 'outline'} className="mt-0.5 text-[10px]">
                        {m.type === 'audio' ? '录音' : '文字'}
                      </Badge>
                      <div className="min-w-0 flex-1">
                        <div className="line-clamp-2 text-sm">
                          {m.type === 'audio' ? m.file_name || '未命名录音' : m.content}
                        </div>
                        {m.note && <div className="mt-0.5 text-xs text-muted-foreground line-clamp-1">{m.note}</div>}
                        <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
                          <span>{m.status}</span>
                          <span>{m.created_at}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ─── 短视频工作台 ─── */}
      {activeTab === 'workbench' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>短视频逐字稿生成</CardTitle>
              <CardDescription>选择目标时长后，后台会把综合语料和提示词合成为逐字稿任务</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {DURATIONS.map((item) => (
                  <Button
                    key={item}
                    variant={duration === item ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setDuration(item)}
                  >
                    {item}
                  </Button>
                ))}
              </div>
              <Button onClick={() => handleGenerateTranscript()} disabled={transcriptBusy}>
                {transcriptBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {transcriptBusy ? '生成中…' : '生成逐字稿'}
              </Button>
              {transcriptStatus && <p className="text-sm text-muted-foreground">{transcriptStatus}</p>}
              {transcriptResult && (
                <div className="rounded-lg border bg-accent/30 p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="text-sm font-medium">逐字稿预览</h4>
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => handleCopy(transcriptResult)}>
                      {rewriteCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                      {rewriteCopied ? '已复制' : '复制'}
                    </Button>
                  </div>
                  <pre className="whitespace-pre-wrap text-sm text-muted-foreground">{transcriptResult}</pre>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <ImageIcon className="h-4 w-4 text-muted-foreground" />
                  <CardTitle className="text-base">短视频封面设计</CardTitle>
                </div>
                <CardDescription>预留封面模板、标题拆分和 Canva/本地设计接口</CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTranscriptStatus('封面设计模块待接入模板和设计 Agent')}
                >
                  启动封面设计
                </Button>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Scissors className="h-4 w-4 text-muted-foreground" />
                  <CardTitle className="text-base">短视频剪辑模块</CardTitle>
                </div>
                <CardDescription>预留素材切片、字幕、B-roll 和成片导出流程</CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTranscriptStatus('剪辑模块待接入素材库和剪辑 Agent')}
                >
                  启动剪辑任务
                </Button>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Newspaper className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-base">公众号生成</CardTitle>
              </div>
              <CardDescription>点击后启动主管写作 Agent，把短视频选题和 IP 语料升级为公众号长文</CardDescription>
            </CardHeader>
            <CardContent>
              <Button size="sm" disabled={transcriptBusy} onClick={handleWechatArticle}>
                <Sparkles className="h-4 w-4" />
                启动公众号生成
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ─── 内容改写 ─── */}
      {activeTab === 'rewrite' && (
        <Card>
          <CardHeader>
            <CardTitle>AI 内容改写</CardTitle>
            <CardDescription>输入文案，AI 根据你的表达习惯和风格偏好进行改写（消耗积分）</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium">选择风格</label>
              <div className="flex gap-2">
                {[
                  { key: 'casual', label: '口语化' },
                  { key: 'formal', label: '正式' },
                  { key: 'viral', label: '爆款体' },
                  { key: 'story', label: '故事化' },
                ].map((s) => (
                  <Button
                    key={s.key}
                    variant={rewriteStyle === s.key ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setRewriteStyle(s.key)}
                  >
                    {s.label}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">原文</label>
              <textarea
                className="min-h-[150px] w-full rounded-md border bg-background p-3 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder="粘贴需要改写的文案…"
                value={rewriteText}
                onChange={(e) => setRewriteText(e.target.value)}
              />
            </div>
            <Button onClick={handleRewrite} disabled={rewriting || !rewriteText.trim()}>
              {rewriting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {rewriting ? '改写中…' : '开始改写'}
            </Button>
            {rewriteResult && (
              <div className="rounded-lg border bg-accent/30 p-4">
                <div className="mb-2 flex items-center justify-between">
                  <h4 className="text-sm font-medium">改写结果</h4>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => handleCopy(rewriteResult)}>
                    {rewriteCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    {rewriteCopied ? '已复制' : '复制'}
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{rewriteResult}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ─── 采访提纲 / 米粒采访 ─── */}
      {activeTab === 'interview' && (
        <Card>
          <CardHeader>
            <CardTitle>采访提纲生成</CardTitle>
            <CardDescription>输入主题，选择采访深度，AI 生成采访问题清单；勾选后可直接创建米粒采访任务</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="输入采访主题，例如：高考志愿填报经验分享"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
              />
              <Button onClick={handleOutline} disabled={outlineLoading || !topic.trim()}>
                {outlineLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4" />}
                生成
              </Button>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">采访深度</label>
              <div className="flex gap-2">
                {(['一般采访', '深度采访'] as const).map((mode) => (
                  <Button
                    key={mode}
                    variant={interviewMode === mode ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setInterviewMode(mode)}
                  >
                    {mode}
                    <span className="ml-1 text-[10px] opacity-70">
                      {mode === '一般采访' ? '4-5 问' : '8-10 问'}
                    </span>
                  </Button>
                ))}
              </div>
            </div>
            {outline && (
              <div className="rounded-lg border bg-accent/30 p-4">
                <div className="mb-2 flex items-center justify-between">
                  <h4 className="text-sm font-medium">采访提纲</h4>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => handleCopy(outline)}>
                    <Copy className="h-3.5 w-3.5" />
                    复制
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{outline}</p>
              </div>
            )}
            <Button variant="default" disabled={interviewBusy || !outline} onClick={handleStartInterview}>
              {interviewBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4" />}
              {interviewBusy ? '创建中…' : '创建米粒采访任务'}
            </Button>
            {interviewStatus && <p className="text-sm text-muted-foreground">{interviewStatus}</p>}
          </CardContent>
        </Card>
      )}

      {/* ─── 海报生成 ─── */}
      {activeTab === 'poster' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>一键生成海报 / 长图</CardTitle>
              <CardDescription>输入文案，AI 生成可发布的海报：短视频封面、朋友圈/小红书配图、公众号长图（消耗积分）</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium">主文案</label>
                <textarea
                  className="min-h-[80px] w-full rounded-md border bg-background p-3 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  placeholder="输入海报主文案，例如：AI员工不是工具，而是有岗位有KPI的业务成员"
                  value={posterText}
                  onChange={(e) => setPosterText(e.target.value)}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium">副标题 / 金句（可选）</label>
                <input
                  className="w-full rounded-md border bg-background p-2.5 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  placeholder="输入副标题或金句"
                  value={posterSubtitle}
                  onChange={(e) => setPosterSubtitle(e.target.value)}
                />
              </div>
              {/* 图片上传区（根据模板动态渲染） */}
              {posterTemplates.find((t) => t.id === posterTemplateId) && (
                <div className="grid gap-3 md:grid-cols-2">
                  {(posterTemplates.find((t) => t.id === posterTemplateId)?.image_elements || []).map((img: any) => (
                    <div key={img.id || img.field} className="rounded-lg border bg-muted/20 p-3">
                      <label className="mb-1.5 block text-sm font-medium">
                        {img.label || img.field}
                        {img.optional && <span className="ml-1 text-[10px] text-muted-foreground">（可选）</span>}
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="file"
                          accept="image/*"
                          className="block w-full text-xs text-muted-foreground file:mr-2 file:rounded-md file:border-0 file:bg-primary/10 file:px-2 file:py-1 file:text-xs file:font-medium file:text-primary hover:file:bg-primary/20"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setUploadingField(img.field);
                            try {
                              const result = await uploadImage(file);
                              setPosterImages((prev) => ({ ...prev, [img.field]: result.url }));
                            } catch (err) {
                              setPosterError(err instanceof Error ? err.message : '上传失败');
                            } finally {
                              setUploadingField('');
                            }
                          }}
                        />
                        {uploadingField === img.field && <Loader2 className="h-4 w-4 animate-spin shrink-0" />}
                        {posterImages[img.field] && (
                          <img src={posterImages[img.field]} alt={img.label} className="h-10 w-10 shrink-0 rounded-md border object-cover" />
                        )}
                      </div>
                      {posterImages[img.field] && (
                        <button
                          className="mt-1 text-[11px] text-destructive hover:text-destructive/80"
                          onClick={() => setPosterImages((prev) => { const n = { ...prev }; delete n[img.field]; return n; })}
                        >
                          移除
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <label className="mb-1.5 block text-sm font-medium">模板</label>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {posterTemplates.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => {
                          setPosterTemplateId(t.id);
                          if (!t.sizes.includes(posterSize)) {
                            setPosterSize(t.default_size || t.sizes[0] || '3:4');
                          }
                        }}
                        className={`group relative overflow-hidden rounded-lg border transition-all ${
                          posterTemplateId === t.id
                            ? 'border-primary ring-2 ring-primary/30'
                            : 'border-border hover:border-primary/50'
                        }`}
                        title={t.description}
                      >
                        <div className="aspect-[5/7] w-full bg-muted">
                          {t.thumbnail ? (
                            <img
                              src={t.thumbnail}
                              alt={t.name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                              {t.name}
                            </div>
                          )}
                        </div>
                        <div className="p-2 text-left">
                          <div className="text-xs font-medium truncate">{t.name}</div>
                          <div className="text-[10px] text-muted-foreground truncate">{t.description}</div>
                        </div>
                        {posterTemplateId === t.id && (
                          <div className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
                            ✓
                          </div>
                        )}
                      </button>
                    ))}
                    {posterTemplates.length === 0 && (
                      <span className="col-span-4 text-xs text-muted-foreground">加载模板中…</span>
                    )}
                  </div>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {posterTemplates.find((t) => t.id === posterTemplateId)?.description || ''}
                  </p>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium">尺寸</label>
                  <div className="flex flex-wrap gap-1.5">
                    {(posterTemplates.find((t) => t.id === posterTemplateId)?.sizes || ['3:4']).map((s) => {
                      const labels: Record<string, string> = { '16:9': '横版 16:9', '3:4': '竖版 3:4', '9:16': '长图 9:16' };
                      return (
                        <Button
                          key={s}
                          variant={posterSize === s ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setPosterSize(s)}
                        >
                          {labels[s] || s}
                        </Button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium">模式</label>
                  <div className="flex gap-1.5">
                    <Button
                      variant={posterScheme === 'template' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPosterScheme('template')}
                    >
                      文字准确
                    </Button>
                    <Button
                      variant={posterScheme === 'aigc' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPosterScheme('aigc')}
                    >
                      AI 自由创作
                    </Button>
                  </div>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {posterScheme === 'template'
                      ? '文字 100% 准确，AI 生成背景后合成'
                      : '纯 AI 生成，中文文字可能不准'}
                  </p>
                </div>
              </div>
              <Button onClick={handleGeneratePoster} disabled={posterGenerating || !posterText.trim()}>
                {posterGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                {posterGenerating ? '生成中…（约 30 秒）' : '生成海报'}
              </Button>
              {posterError && <p className="text-sm text-destructive">{posterError}</p>}
              {posterImageUrl && (
                <div className="rounded-lg border bg-accent/30 p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <h4 className="text-sm font-medium">生成结果</h4>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => handleCopy(posterImageUrl)}>
                        <Copy className="h-3.5 w-3.5" />
                        复制链接
                      </Button>
                      <a href={posterImageUrl} download>
                        <Button variant="outline" size="sm" className="h-7 text-xs">
                          <Download className="h-3.5 w-3.5" />
                          下载
                        </Button>
                      </a>
                    </div>
                  </div>
                  <div className="flex justify-center rounded-md bg-background/60 p-3">
                    <img
                      src={posterImageUrl}
                      alt="生成的海报"
                      className="max-h-[480px] rounded-md border object-contain"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>历史海报</CardTitle>
                  <CardDescription>共 {posterHistory.length} 张，可重新下载</CardDescription>
                </div>
                <Button variant="ghost" size="icon" onClick={loadPosters}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {posterHistory.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                  <ImageIcon className="mb-2 h-6 w-6" />
                  <p className="text-sm">还没有生成过海报</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                  {posterHistory.map((p) => (
                    <a key={p.id} href={p.image_path} target="_blank" rel="noreferrer" className="group">
                      <div className="overflow-hidden rounded-md border bg-muted/20">
                        <img
                          src={p.image_path}
                          alt={p.title || '海报'}
                          className="aspect-[3/4] w-full object-cover transition-transform group-hover:scale-105"
                        />
                      </div>
                      <div className="mt-1 truncate text-xs text-muted-foreground">
                        {p.title || p.created_at}
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      </div>

      {/* 右栏 1/3：热点板块（常驻，不随 Tab 切换） */}
      <div className="min-w-0 xl:col-span-1">
        <HotspotsPage compact />
      </div>
    </div>
  );
}
